import { ArrowDown, ArrowUp, CircleHelp } from 'lucide-react';
import type { EngagementFieldAvailability } from '@/api/generated/model';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { kpiMeta } from '../lib/engagementColors';
import {
  EngagementBusy,
  HoverHint,
  NotReadyBadge,
  engagementReadyState,
} from './EngagementNotReady';

export interface EngagementKpiView {
  key?: string;
  label: string;
  value: number | null;
  delta: number | null;
  description?: string;
  availability?: EngagementFieldAvailability;
  note?: string;
}

function formatCount(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat().format(value);
}

/** Hover help from the API description (business copy already includes PoP wording). */
function kpiCalculationHint(metric: EngagementKpiView): string {
  return (
    metric.description?.trim() ||
    `${metric.label} count for the selected date range. Percentage change is shown when a prior period is available for comparison.`
  );
}

/**
 * Figma KPI tile: small tinted icon + label, oversized count, then a coloured
 * delta against the explicitly named comparison window.
 */
export function EngagementKpiCards({
  metrics,
  comparisonLabel,
  className,
  isLoading,
}: {
  metrics: EngagementKpiView[];
  /** e.g. "May 13 - May 19" — the prior window the delta is measured against. */
  comparisonLabel?: string;
  className?: string;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <div className={cn('grid gap-4', className)} aria-busy="true">
        <EngagementBusy className="rounded-xl border border-border/40 bg-[#1a1a1c]" />
      </div>
    );
  }
  return (
    <div className={cn('grid gap-4', className)}>
      {metrics.map((metric) => {
        const state = engagementReadyState(metric.availability);
        const live = state === 'live';
        const { icon: Icon, color } = kpiMeta(metric.key);
        const rising = (metric.delta ?? 0) >= 0;
        const DeltaIcon = rising ? ArrowUp : ArrowDown;
        const calculationHint = kpiCalculationHint(metric);

        return (
          <Card
            key={metric.key ?? metric.label}
            className="border-border/40 bg-[#1a1a1c] shadow-none"
          >
            <CardContent className="space-y-2 p-5">
              <div className="flex items-center justify-between gap-2">
                <HoverHint
                  text={calculationHint}
                  label={`How ${metric.label} is calculated. ${calculationHint}`}
                  className="cursor-help"
                >
                  <span className="flex items-center gap-2">
                    <Icon className="size-4" style={{ color }} aria-hidden />
                    <span className="text-sm font-medium text-muted-foreground">
                      {metric.label}
                    </span>
                    <CircleHelp
                      className="size-3.5 shrink-0 text-muted-foreground/80"
                      aria-hidden
                    />
                  </span>
                </HoverHint>
                <NotReadyBadge state={state} hint={metric.note ?? metric.description} />
              </div>
              <p
                className={cn(
                  'text-[32px] font-bold leading-none tracking-tight tabular-nums',
                  !live && 'text-muted-foreground',
                )}
              >
                {live ? formatCount(metric.value) : '—'}
              </p>
              {live && metric.delta != null && comparisonLabel ? (
                <HoverHint
                  text={calculationHint}
                  label={`How ${metric.label} change is calculated. ${calculationHint}`}
                  className="cursor-help"
                >
                  <p className="flex items-center gap-1 text-xs">
                    <span
                      className={cn(
                        'inline-flex items-center gap-0.5 font-semibold',
                        rising ? 'text-emerald-400' : 'text-rose-400',
                      )}
                    >
                      <DeltaIcon className="size-3" aria-hidden />
                      {Math.abs(metric.delta).toFixed(1)}%
                    </span>
                    <span className="text-muted-foreground">vs {comparisonLabel}</span>
                  </p>
                </HoverHint>
              ) : (
                <p className="text-xs text-muted-foreground">No prior comparison</p>
              )}
              {metric.note ? (
                <p className="text-[11px] leading-4 text-sky-400/90">{metric.note}</p>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
